import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  EntityWithPhones, FieldStatus, FieldStatusInput, GetEntityById2GQL, PhoneDetails, UpdatePhoneGQL,
  UpdatePhoneNumberInput
} from 'generated/graphql';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import {
  AddOrUpdatePhoneDialog
} from 'entity/dialogs/add-or-update-phone/add-or-update-phone.dialog';
import {
  SharedContactInfoDialog
} from 'entity/dialogs/shared-contact-info/shared-contact-info.dialog';
import { getSafe, sortByBest } from 'helpers';
import { EntityDuplicateEntry, EntityDuplicateFinderService } from 'entity/helpers/entity-duplicate-finder.service';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-entity-phones-table',
  templateUrl: './entity-phones-table.component.html'
})
export class EntityPhonesTableComponent
  extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  isSaving = false;

  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() showTaskDoneMark: boolean;
  @Input() readonly: boolean;

  entity: EntityWithPhones.Fragment = null;
  showBad = false;

  private dupesEntry: EntityDuplicateEntry;

  sortByBest = sortByBest;

  constructor(
    private _getEntityGQL: GetEntityById2GQL,
    private _updatePhoneGQL: UpdatePhoneGQL,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _duplicateFinder: EntityDuplicateFinderService,
    private _toastrService: ToastrService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {

    super();
  }

  ngOnInit() {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId || !!entityId),
      switchMap(({ campaignId, entityId }) => this._getEntityGQL.watch({
        entityId,
        campaignId,
        withToplines: true,
        withPhones: true,
        withBestPhone: true
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges.pipe(takeUntil(this.disposer))),
      map(({ data }) => data?.getEntity),
      tap((entity) => this.entity = entity),
      switchMap(async entity => {
        if (this.readonly) {
          return { entity, dupesEntry : null };
        }
        const dupesEntry = await this._duplicateFinder.initEntityDuplicateEntry(entity.id, entity.campaignContextId);
        return { entity, dupesEntry };
      }),
      takeUntil(this.disposer)
    ).subscribe(({entity, dupesEntry}) => {
      this.entity = entity as any;
      this.dupesEntry = dupesEntry;
      this._cd.markForCheck();
    });


  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onAddNew() {
    AddOrUpdatePhoneDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        phone: null
      }
    });
  }

  onEdit(phone: PhoneDetails.Fragment) {
    AddOrUpdatePhoneDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        phone
      }
    });
  }

  onSetStatus(phone: PhoneDetails.Fragment, status: FieldStatusInput) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    if ([FieldStatusInput.Bad, FieldStatusInput.DoNotContact].includes(status) ) {
      const shouldMarkBad = confirm(
        `Are you sure you would like to mark this phone number as ${status.split('_').join(' ')}?`
        );
      if (!shouldMarkBad) {
        this.isSaving = false;
        return;
      }
    }

    const input: UpdatePhoneNumberInput = {
      phoneNumberId: phone.id,
      campaignId: this.campaignId,
      entityId: this.entity.id,
      changes: {
        status,
        number: phone.number,
        numberType: phone.numberType,
        ext: phone.ext,
        subscribe: phone.subscribed
      }
    };

    this._updatePhoneGQL.mutate({ input }).pipe(
      finalize(() => this.isSaving = false),
    ).subscribe(
      (_) => this._toastrService.success('Changes saved'),
    );
  }

  onSetSubscribed(phone: PhoneDetails.Fragment, subscribed: boolean) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    const input: UpdatePhoneNumberInput = {
      phoneNumberId: phone.id,
      campaignId: this.campaignId,
      entityId: this.entity.id,
      changes: {
        subscribe: subscribed
      }
    };

    this._updatePhoneGQL.mutate({ input }).pipe(
      finalize(() => this.isSaving = false),
    ).subscribe(
      (_) => this._toastrService.success('Changes saved'),
    );
  }

  isSharedPhone(phone: string, ext: string) {
    if (this.readonly) {
      return false;
    }
    return this.dupesEntry?.phoneMatches(phone, ext).length > 0;
  }

  isVerified(item: PhoneDetails.Fragment) {
    return item.status === FieldStatus.Verified;
  }

  isBad(item: PhoneDetails.Fragment) {
    return getSafe(() => item.status === 'bad', true);
  }

  isDoNotContact(item: PhoneDetails.Fragment) {
    return item.status === FieldStatus.DoNotContact;
  }

  getBadCount(items: PhoneDetails.Fragment[]) {
    return getSafe(() => items.filter(i => this.isBad(i)).length, 0);
  }

  onFlagSharedInfoClick(phone: PhoneDetails.Fragment) {
    const entityMatches = this.dupesEntry?.phoneMatches(phone.number, phone.ext);
    // this.setQueryParamsModalState('phone', phone.id);
    SharedContactInfoDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        phone,
        entityMatches
      }
    });
  }

  setQueryParamsModalState(type: 'phone' | 'email', id: string) {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { potentialDupesFor: `${type}-${id}` },
      queryParamsHandling: 'merge'
    });
  }

}
